import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-format-detail',
  templateUrl: './register-format-detail.component.html',
  styleUrls: ['./register-format-detail.component.scss'],
  standalone: false
})
export class RegisterFormatDetailComponent implements OnChanges {

  @Input() formatDetailContract: FormatDetailModel = new FormatDetailModel();
  @Input() formatModel: FormatModel = new FormatModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  formatDetailId: number = 0;
  formFormatDetail: FormGroup = new FormGroup({});
  listAlignment: CustomListDropdown[] = [{ label: 'Left', value: 'left' }, { label: 'Right', value: 'right' },
  { label: 'Center', value: 'center' }, { label: 'Justify', value: 'justify' }];
  listBand: CustomListDropdown[] = [{ label: 'Head', value: 'head' }, { label: 'Body', value: 'body' },
  { label: 'Body 2', value: 'body2' }, { label: 'Foot', value: 'foot' }];
  listBold: CustomListDropdown[] = [{ label: 'Normal', value: 'normal' }, { label: 'Bold', value: 'bold' }, { label: 'Lighter', value: 'lighter' }];
  listBorder: CustomListDropdown[] = [{ label: 'NoBorder', value: 'noborder' }, { label: 'Border', value: 'border' }, { label: 'Underline', value: 'underline' }];
  listType: CustomListDropdown[] = [{ label: 'Label', value: 'label' }, { label: 'P', value: 'p' }, { label: 'Img', value: 'img' }];

  constructor(private formBuilder: FormBuilder,
    private formatDetailService: FormatDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.formatDetailContract);
  }

  ngOnChanges(changes: SimpleChanges) {
    const formatDetailContract = changes['formatDetailContract'];
    if (formatDetailContract) {
      if (this.formatDetailContract.formatDetailId === 0) {
        this.formatDetailId = 0;
        this.formatDetailContract = new FormatDetailModel();
      } else {
        this.formatDetailId = this.formatDetailContract.formatDetailId;
      }
      this.validateForm(this.formatDetailContract);
    }
  }

  clearForm() {
    this.formatDetailId = 0;
    this.formatDetailContract = new FormatDetailModel();
    this.validateForm(this.formatDetailContract);
    this.isFormView.emit(false);
  }

  changeFormatDetailForm() {
    const name = this.formFormatDetail.get("name")?.value;
    if (name.trim() !== "") {
      this.getFormatDetailByFormatAndName(name);
    } else {
      this.clearForm();
    }
  }

  changeType() {
    const type = this.formFormatDetail.get("type")?.value;//1:Label - 2:P - 3:Img
    if (type === "label") {
      setTimeout(() => this.formFormatDetail.controls["text"].enable(), 100);
    } else {
      this.formFormatDetail.controls["text"].setValue("");
      setTimeout(() => this.formFormatDetail.controls["text"].disable(), 100);
    }
  }

  async getFormatDetailByFormatAndName(name: string) {
    try {
      this.formatDetailContract = await firstValueFrom(this.formatDetailService.getFormatDetailByFormatAndName(this.formatDetailContract.formatId, name));
      this.formatDetailId = this.formatDetailContract.formatDetailId;
      this.validateForm(this.formatDetailContract);
    } catch (error) {
      console.error('Error al obtener el detalle del formato por nombre.', error);
    }
  }

  async send() {
    Object.keys(this.formFormatDetail.controls).forEach((key) => {
      const control = this.formFormatDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formFormatDetail.valid) {
      const modelFormatDetail = this.formFormatDetail.getRawValue() as FormatDetailModel;
      modelFormatDetail.formatId = this.formatModel.formatId;

      if (modelFormatDetail.description?.trim() === "") modelFormatDetail.description = null;
      if (modelFormatDetail.text?.trim() === "") modelFormatDetail.text = null;

      if (this.formatDetailId === 0) {
        try {
          await firstValueFrom(this.formatDetailService.createFormatDetail(modelFormatDetail));
          this.updateListDetail.emit(true);
          this.toastHandlerService.showToast('success', 'El detalle del formato fue creado exitosamente.');
          this.clearForm();
        } catch (error) {
          console.error('Error al crear el detalle del formato.', error);
        }
      } else {
        this.formatDetailContract.description = modelFormatDetail.description;
        this.formatDetailContract.band = modelFormatDetail.band;
        this.formatDetailContract.type = modelFormatDetail.type;
        this.formatDetailContract.visible = modelFormatDetail.visible;
        this.formatDetailContract.left = modelFormatDetail.left;
        this.formatDetailContract.top = modelFormatDetail.top;
        this.formatDetailContract.width = modelFormatDetail.width;
        this.formatDetailContract.height = modelFormatDetail.height;
        this.formatDetailContract.fontSize = modelFormatDetail.fontSize;
        this.formatDetailContract.bold = modelFormatDetail.bold;
        this.formatDetailContract.alignment = modelFormatDetail.alignment;
        this.formatDetailContract.border = modelFormatDetail.border;
        this.formatDetailContract.orderTable = modelFormatDetail.orderTable;
        this.formatDetailContract.text = modelFormatDetail.text;
        this.formatDetailContract.printFormat = modelFormatDetail.printFormat?.trim() || null;
        try {
          await firstValueFrom(this.formatDetailService.updateFormatDetail(this.formatDetailContract));
          this.updateListDetail.emit(true);
          this.toastHandlerService.showToast('success', 'El detalle del formato fue modificado exitosamente.');
          this.clearForm();
        } catch (error) {
          console.error('Error al actualizar el detalle del formato.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxLength: 'El campo Nombre no puede superar 32 caracteres.',
      },
      description: {
        required: 'El campo Descripción es obligatorio.',
        maxLength: 'El campo Descripción no puede superar 64 caracteres.',
      },
      band: {
        required: 'El campo Band es obligatorio.',
      },
      type: {
        required: 'El campo Tipo es obligatorio.',
      },
      left: {
        required: 'El campo Eje X es obligatorio.',
        min: 'El valor del campo Eje X no puede ser menor a 0.',
        max: 'El valor del campo Eje X no puede ser mayor a 99999.99.'
      },
      top: {
        required: 'El campo Eje Y es obligatorio.',
        min: 'El valor del campo Eje Y no puede ser menor a 0.',
        max: 'El valor del campo Eje Y no puede ser mayor a 99999.99.'
      },
      width: {
        required: 'El campo Width es obligatorio.',
        min: 'El valor del campo Width no puede ser menor a 0.',
        max: 'El valor del campo Width no puede ser mayor a 99999.99.'
      },
      height: {
        required: 'El campo Height es obligatorio.',
        min: 'El valor del campo Height no puede ser menor a 0.',
        max: 'El valor del campo Height no puede ser mayor a 99999.99.'
      },
      fontSize: {
        required: 'El campo FontSize es obligatorio.',
        min: 'El valor del campo FontSize no puede ser menor a 0.',
        max: 'El valor del campo FontSize no puede ser mayor a 99999.99.'
      },
      bold: {
        required: 'El campo Bold es obligatorio.',
      },
      alignment: {
        required: 'El campo Alignment es obligatorio.',
      },
      border: {
        required: 'El campo Border es obligatorio.',
      },
      text: {
        maxLength: 'El campo Text no puede superar 100 caracteres.',
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formFormatDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(formatDetail: FormatDetailModel) {
    this.formFormatDetail = this.formBuilder.group({
      name: [formatDetail.name, [Validators.required, Validators.maxLength(32)]],
      description: [formatDetail.description, [Validators.maxLength(64)]],
      band: [formatDetail.band, [Validators.required]],
      type: [formatDetail.type, [Validators.required]],
      visible: [formatDetail.visible],
      left: [formatDetail.left, [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      top: [formatDetail.top, [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      width: [formatDetail.width, [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      height: [formatDetail.height, [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      fontSize: [formatDetail.fontSize, [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      bold: [formatDetail.bold, [Validators.required]],
      alignment: [formatDetail.alignment, [Validators.required]],
      border: [formatDetail.border, [Validators.required]],
      text: [formatDetail.text, [Validators.maxLength(100)]],
      orderTable: [formatDetail.orderTable, [Validators.min(0), Validators.max(9999)]],
      printFormat: [formatDetail.printFormat, [Validators.maxLength(20)]],
    });
    this.changeType();
  }

}
